import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';

import landmarkApi from 'api/landmarkApi';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import FormField from 'components/AddLocation/FormField';
import InputValidation from 'components/App/InputValidation';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Pagination from 'components/Pagination';
import SelectValidation from 'components/App/SelectValidation';
import { LOCATIONS, themeColor } from 'constants/index';
import images from 'assets/images';
import { toast } from 'react-toastify';
import { handleError } from 'utils';

import AreaTable from './components/AreaTable';
import styles from './ManagerAreas.module.scss';

const TITLE = 'Quản lý khu vực';
const DEFAULT_LIMIT = 20;

const breadcrumbList = [
  {
    name: 'Trang chủ',
    isActive: false,
    link: '',
  },
  {
    name: TITLE,
    isActive: true,
    link: LOCATIONS.LIST_OF_AREAS,
  },
];

const mapAreaItem = (item, marketMap) => ({
  id: item._id || item.id,
  name: item.name || '',
  nameEN: item.nameEN || '',
  market: marketMap[item.market?._id || item.market?.id]
    || item.market?.name
    || item.market?.nameEN
    || '',
  totalPlaces: item.totalPlaces ?? 0,
});

function ManagerAreas(props) {
  const {
    marketList,
    isLoadingMarketList,
    isLoadingLandmarkDetails,
    isLoadingCreateLandmark,
    isLoadingUpdateLandmark,
    isLoadingDeleteLandmark,
    actionCreateLandmark,
    actionDeleteLandmark,
    actionGetLandmarkDetails,
    actionGetMarketList,
    actionUpdateLandmark,
  } = props;
  const history = useHistory();
  const [areas, setAreas] = useState({ data: [], total: 0 });
  const [rawAreas, setRawAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState(
    () => new URLSearchParams(history.location.search).get('q') || '',
  );
  const [filterMarket, setFilterMarket] = useState(
    () => new URLSearchParams(history.location.search).get('market') || '',
  );
  const [isOpenCreateArea, setIsOpenCreateArea] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [deleteArea, setDeleteArea] = useState({
    id: '',
    isOpen: false,
  });

  const currentPage = useMemo(
    () => Number(new URLSearchParams(history.location.search).get('page') || 1),
    [history.location.search],
  );
  const submittedSearch = useMemo(
    () => new URLSearchParams(history.location.search).get('q') || '',
    [history.location.search],
  );
  const submittedMarket = useMemo(
    () => new URLSearchParams(history.location.search).get('market') || '',
    [history.location.search],
  );
  const marketMap = useMemo(
    () => marketList.reduce((acc, item) => ({
      ...acc,
      [item._id || item.id]: item.name || item.nameEN || '',
    }), {}),
    [marketList],
  );

  const refreshAreas = useCallback(async () => {
    const payload = {
      page: currentPage,
      limit: DEFAULT_LIMIT,
    };

    if (submittedSearch) {
      payload.q = submittedSearch;
    }

    if (submittedMarket) {
      payload.market = submittedMarket;
    }

    const response = await landmarkApi.getLandmarks(payload);
    const rawData = response?.payload?.data ?? [];
    const total = response?.payload?.total ?? rawData.length;

    setRawAreas(rawData);
    setAreas({
      data: rawData,
      total,
    });
  }, [currentPage, submittedMarket, submittedSearch]);

  const validation = useFormik({
    initialValues: {
      name: '',
      nameEN: '',
      market: '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Vui lòng nhập tên khu vực')
        .max(100, 'Tên khu vực không quá 100 ký tự'),
      nameEN: Yup.string()
        .required('Vui lòng nhập tên khu vực')
        .max(100, 'Tên khu vực không quá 100 ký tự'),
      market: Yup.string()
        .required('Vui lòng chọn thị trường'),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = {
          name: values.name,
          nameEN: values.nameEN,
          market: values.market,
        };

        if (editingArea?.id) {
          actionUpdateLandmark({
            id: editingArea.id,
            ...payload,
            callback: async () => {
              await refreshAreas();
              resetForm();
              setEditingArea(null);
              setIsOpenCreateArea(false);
              toast.success('Cập nhật khu vực thành công');
            },
          });
          return;
        }

        actionCreateLandmark({
          ...payload,
          callback: async () => {
            await refreshAreas();
            resetForm();
            setEditingArea(null);
            setIsOpenCreateArea(false);
            toast.success('Thêm khu vực thành công');
          },
        });
        return;
      } finally {
        setSubmitting(false);
      }
    },
  });

  const marketOptions = useMemo(
    () => marketList.map((market) => ({
      ...market,
      label: market.name,
      value: market._id || market.id,
    })).sort((a, b) => a.label.localeCompare(b.label)),
    [marketList],
  );

  const selectedMarketValue = useMemo(() => {
    const matchedMarket = marketOptions.find(
      (item) => item.value === validation.values.market,
    );

    return matchedMarket?.value ?? '';
  }, [marketOptions, validation.values.market]);

  const filterMarketValue = useMemo(() => {
    const matchedMarket = marketOptions.find(
      (item) => item.value === filterMarket,
    );

    return matchedMarket?.value ?? '';
  }, [filterMarket, marketOptions]);

  const totalItems = areas.total || 0;
  const totalPage = Math.ceil(totalItems / DEFAULT_LIMIT);
  const paginatedAreas = useMemo(
    () => rawAreas.map((item) => mapAreaItem(item, marketMap)),
    [marketMap, rawAreas],
  );

  const onOpenCreateArea = useCallback(() => {
    validation.resetForm();
    setEditingArea(null);
    setIsOpenCreateArea(true);
  }, [validation]);

  const onCloseCreateArea = useCallback(() => {
    validation.resetForm();
    setEditingArea(null);
    setIsOpenCreateArea(false);
  }, [validation]);

  const onOpenEditArea = useCallback((item) => {
    setIsOpenCreateArea(true);
    actionGetLandmarkDetails({
      id: item.id,
      callback: (response) => {
        const details = response?.payload || response || {};

        setEditingArea({
          id: details._id || details.id || item.id,
        });
        validation.setValues({
          name: details.name || '',
          nameEN: details.nameEN || '',
          market: details.market?._id || details.market?.id || '',
        });
      },
      errorCallback: () => {
        setIsOpenCreateArea(false);
      },
    });
  }, [actionGetLandmarkDetails, validation]);

  const onCloseDeleteArea = useCallback(() => {
    setDeleteArea((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const onSubmitDeleteArea = useCallback(() => {
    actionDeleteLandmark({
      id: deleteArea.id,
      callback: async () => {
        await refreshAreas();
        setDeleteArea({
          id: '',
          isOpen: false,
        });
        toast.success('Xóa khu vực thành công');
      },
    });
  }, [actionDeleteLandmark, deleteArea.id, refreshAreas]);

  const handleSearchSubmit = useCallback(() => {
    const searchParams = new URLSearchParams(history.location.search);

    if (search.trim()) {
      searchParams.set('q', search.trim());
    } else {
      searchParams.delete('q');
    }

    if (filterMarket) {
      searchParams.set('market', filterMarket);
    } else {
      searchParams.delete('market');
    }

    searchParams.set('page', '1');
    history.push(`?${searchParams.toString()}`);
  }, [filterMarket, history, search]);

  const onResetForm = useCallback(() => {
    setSearch('');
    setFilterMarket('');
    history.push('?page=1');
  }, [history]);

  const onChangePage = useCallback((selected) => {
    const nextPage = selected.selected + 1;
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set('page', String(nextPage));
    history.push(`?${searchParams.toString()}`);
  }, [history]);

  const onChangeMarket = useCallback((selected) => {
    validation.setFieldValue(
      'market',
      selected?.value || '',
      true,
    );
  }, [validation]);

  const onChangeFilterMarket = useCallback((selected) => {
    setFilterMarket(selected?.value || '');
  }, []);

  useEffect(() => {
    actionGetMarketList({
      page: 1,
      limit: 500,
    });
  }, [actionGetMarketList]);

  useEffect(() => {
    setSearch(submittedSearch);
    setFilterMarket(submittedMarket);
  }, [submittedMarket, submittedSearch]);

  useEffect(() => {
    let cancelled = false;

    const fetchAreas = async () => {
      try {
        setIsLoading(true);
        const payload = {
          page: currentPage,
          limit: DEFAULT_LIMIT,
        };

        if (submittedSearch) {
          payload.q = submittedSearch;
        }

        if (submittedMarket) {
          payload.market = submittedMarket;
        }

        const response = await landmarkApi.getLandmarks(payload);
        const rawData = response?.payload?.data ?? [];
        const total = response?.payload?.total ?? rawData.length;

        if (!cancelled) {
          setRawAreas(rawData);
          setAreas({
            data: rawData,
            total,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setRawAreas([]);
          setAreas({ data: [], total: 0 });
        }
        handleError(error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAreas();

    return () => {
      cancelled = true;
    };
  }, [currentPage, submittedMarket, submittedSearch]);

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name={TITLE} />

        <Container fluid>
          <Breadcrumb pageTitle={TITLE} breadcrumbList={breadcrumbList} />

          <Loading loading={isLoading || isLoadingMarketList} loaderOnPage />

          <Card className="Section">
            <Row>
              <Col md="5">
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="position-relative">
                  <Input
                    placeholder="Tìm kiếm khu vực"
                    style={{ paddingLeft: '36px' }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit();
                      }
                    }}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </Col>

              <Col md="3">
                <Label className="Section__Search__Label">Bộ lọc</Label>
                <SelectValidation
                  name="market"
                  placeholder="Thị trường"
                  options={marketOptions}
                  value={filterMarketValue}
                  onChange={onChangeFilterMarket}
                  isDisabled={isLoadingMarketList}
                  isHiddenSearchable
                  selectedClassName={styles.FilterSelect}
                />
              </Col>

              <Col md="4">
                <div className="d-flex align-items-end h-100 mb-24 mb-md-0 gap-3">
                  <Button
                    outline
                    color={themeColor.LIGHT}
                    onClick={onResetForm}
                    style={{ minWidth: '119px' }}
                  >
                    Nhập lại
                  </Button>
                  <Button
                    onClick={handleSearchSubmit}
                    style={{ minWidth: '119px' }}
                  >
                    Tìm kiếm
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          <Card className="Section">
            <div className="Section__Header">
              <Label className="Table__Body__Total mb-0">
                {`${totalItems} khu vực`}
              </Label>
              <Button
                classes="Button__Add"
                fontSize="13px"
                type="button"
                onClick={onOpenCreateArea}
              >
                <i className="fas fa-plus" />
                Thêm khu vực
              </Button>
            </div>

            <AreaTable
              areas={paginatedAreas}
              onEdit={onOpenEditArea}
              onDelete={(item) => setDeleteArea({
                id: item.id,
                isOpen: true,
              })}
            />
          </Card>

          <Pagination
            page={currentPage}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />
        </Container>
      </div>

      <Modal
        isOpen={isOpenCreateArea}
        toggle={onCloseCreateArea}
        size="md"
        fade
        style={{ maxWidth: '779px' }}
      >
        <ModalHeader
          toggle={onCloseCreateArea}
          close={<AppCloseModalButton onClick={onCloseCreateArea} />}
          className="justify-content-between"
        >
          {editingArea ? 'Chỉnh sửa khu vực' : 'Thêm khu vực'}
        </ModalHeader>

        <ModalBody>
          {isLoadingLandmarkDetails ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              Đang tải...
            </div>
          ) : (
            <form onSubmit={validation.handleSubmit}>
              <FormField label="Thị trường" isRequired>
                <SelectValidation
                  name="market"
                  placeholder="Chọn thị trường"
                  options={marketOptions}
                  validation={validation}
                  value={selectedMarketValue}
                  onChange={onChangeMarket}
                  isDisabled={isLoadingMarketList}
                  isHiddenSearchable
                />
              </FormField>

              <FormField label="Tên khu vực - VI" isRequired>
                <InputValidation
                  isCustomOnChange
                  name="name"
                  placeholder="Nhập tên"
                  validation={validation}
                  onChange={validation.handleChange}
                  limitCharacter={100}
                  isShowCounter
                />
              </FormField>

              <FormField label="Tên khu vực - EN" isRequired>
                <InputValidation
                  name="nameEN"
                  placeholder="Nhập tên"
                  validation={validation}
                  onChange={validation.handleChange}
                  limitCharacter={100}
                  isShowCounter
                />
              </FormField>

              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  outline
                  color={themeColor.LIGHT}
                  type="button"
                  onClick={onCloseCreateArea}
                  style={{ minWidth: '96px' }}
                >
                  Huỷ
                </Button>

                <Button
                  type="submit"
                  loading={
                    validation.isSubmitting || isLoadingCreateLandmark || isLoadingUpdateLandmark
                  }
                  disabled={
                    validation.isSubmitting || isLoadingCreateLandmark || isLoadingUpdateLandmark
                  }
                  style={{ minWidth: '96px' }}
                >
                  {editingArea ? 'Cập nhật' : 'Tạo'}
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>

      <ModalRequestAction
        isOpen={deleteArea.isOpen}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xoá \n khu vực này không?'}
        onToggle={onCloseDeleteArea}
        onCloseModal={onCloseDeleteArea}
        onConfirm={onSubmitDeleteArea}
        isLoadingConfirm={isLoadingDeleteLandmark}
      />
    </>
  );
}

ManagerAreas.propTypes = {
  marketList: PropTypes.instanceOf(Array),
  isLoadingMarketList: PropTypes.bool,
  isLoadingLandmarkDetails: PropTypes.bool,
  isLoadingCreateLandmark: PropTypes.bool,
  isLoadingUpdateLandmark: PropTypes.bool,
  isLoadingDeleteLandmark: PropTypes.bool,
  actionCreateLandmark: PropTypes.func,
  actionDeleteLandmark: PropTypes.func,
  actionGetLandmarkDetails: PropTypes.func,
  actionGetMarketList: PropTypes.func,
  actionUpdateLandmark: PropTypes.func,
};

ManagerAreas.defaultProps = {
  marketList: [],
  isLoadingMarketList: false,
  isLoadingLandmarkDetails: false,
  isLoadingCreateLandmark: false,
  isLoadingUpdateLandmark: false,
  isLoadingDeleteLandmark: false,
  actionCreateLandmark: () => {},
  actionDeleteLandmark: () => {},
  actionGetLandmarkDetails: () => {},
  actionGetMarketList: () => {},
  actionUpdateLandmark: () => {},
};

export default ManagerAreas;
